<script>
import ChartingEngulfingPlot from './ChartingEngulfingPlot.vue';
import { formatNumberLabel } from '../adapters/chartingAdapterUtils';
import { useChartingEngulfingDetail } from '../composables/useChartingEngulfingDetail';

export default {
    name: 'ChartingEngulfingInspector',
    components: { ChartingEngulfingPlot },
    props: {
        candidate: { type: Object, required: true },
        ticker: { type: String, required: true },
        anchor: { required: true }
    },
    setup(props) {
        const resource = useChartingEngulfingDetail(props);
        return { detail: resource.data, loading: resource.isLoading, error: resource.error,
            retry: () => resource.refresh().catch(() => {}), formatNumberLabel };
    },
    data: () => ({ positionStyle: { visibility: 'hidden' } }),
    watch: {
        loading() { this.$nextTick(this.updatePosition); },
        detail() { this.$nextTick(this.updatePosition); }
    },
    mounted() {
        this.updatePosition();
        this.$refs.close.focus();
        document.addEventListener('mousedown', this.onOutside, true);
        document.addEventListener('touchstart', this.onOutside, true);
        document.addEventListener('keydown', this.onKeydown, true);
        window.addEventListener('resize', this.updatePosition);
    },
    beforeDestroy() {
        document.removeEventListener('mousedown', this.onOutside, true);
        document.removeEventListener('touchstart', this.onOutside, true);
        document.removeEventListener('keydown', this.onKeydown, true);
        window.removeEventListener('resize', this.updatePosition);
    },
    methods: {
        close(restoreFocus = true) {
            if (restoreFocus && this.anchor?.isConnected) this.anchor.focus();
            this.$emit('close');
        },
        onOutside(event) {
            if (!this.$el.contains(event.target) && !this.anchor?.contains(event.target)) this.close(false);
        },
        onKeydown(event) {
            if (event.key !== 'Escape') return;
            event.preventDefault();
            event.stopPropagation();
            this.close();
        },
        updatePosition() {
            if (!this.$el?.parentElement || !this.anchor?.isConnected) return;
            const parent = this.$el.parentElement.getBoundingClientRect();
            const anchor = this.anchor.getBoundingClientRect();
            const panel = this.$el.getBoundingClientRect();
            const rightOfBar = anchor.right - parent.left + 8;
            const leftOfBar = anchor.left - parent.left - panel.width - 8;
            // Keep the selected monthly bar visible when either side has room.
            const preferredLeft = rightOfBar + panel.width <= parent.width - 8 ? rightOfBar
                : leftOfBar >= 8 ? leftOfBar : anchor.left - parent.left;
            const left = Math.max(8, Math.min(preferredLeft, parent.width - panel.width - 8));
            let top = anchor.bottom - parent.top + 8;
            if (top + panel.height > parent.height - 8) top = anchor.top - parent.top - panel.height - 8;
            top = Math.max(8, Math.min(top, parent.height - panel.height - 8));
            this.positionStyle = { left: `${left}px`, top: `${top}px`, visibility: 'visible' };
        }
    }
};
</script>

<template>
    <section class="charting-engulfing-inspector" :style="positionStyle" role="dialog" :aria-label="'Envolvente de ' + candidate.monthLabel + ' · ' + ticker"
        @pointerdown.stop @mousedown.stop @touchstart.stop @touchmove.stop @wheel.stop @click.stop>
        <header class="charting-engulfing-inspector__header">
            <div><div class="charting-engulfing-inspector__eyebrow">LUPA DE LA ENVOLVENTE · {{ ticker }}</div><h3>{{ candidate.monthLabel }}</h3></div>
            <button ref="close" type="button" class="charting-engulfing-inspector__close" aria-label="Cerrar desglose de la envolvente" @click="close()">×</button>
        </header>
        <div v-if="candidate.provisional" class="charting-engulfing-inspector__provisional">En formación: el mes no ha cerrado y el tipo puede cambiar.</div>
        <div v-if="loading" class="charting-engulfing-inspector__status" role="status"><ProgressSpinner /> Cargando semanas de este mes…</div>
        <div v-else-if="error" class="charting-engulfing-inspector__status" role="alert">
            <span>No se pudo cargar el desglose.</span><button type="button" @click="retry">Reintentar</button>
        </div>
        <template v-else-if="detail">
            <div class="charting-engulfing-inspector__classification" :class="{ 'is-unknown': detail.kind === 'unknown' }" role="status">{{ detail.label }}</div>
            <ChartingEngulfingPlot v-if="detail.weeks.length" :candidate="candidate" :detail="detail" />
            <div v-if="detail.weeks.length" class="charting-engulfing-inspector__guide">┄ Mínimo del mes anterior: {{ formatNumberLabel(candidate.previous.low) }}</div>
            <ol v-if="detail.events.length" class="charting-engulfing-inspector__events">
                <li v-for="(event, index) in detail.events" :key="event.label"><b>{{ index + 1 }}</b><span>{{ event.label }}<strong>{{ formatNumberLabel(event.price) }} · {{ event.dateLabel }}</strong></span></li>
            </ol>
            <p class="charting-engulfing-inspector__explanation">{{ detail.explanation }}</p>
            <p v-if="detail.weeks.some(week => week.partial)" class="charting-engulfing-inspector__footnote">* Semana parcial: solo las sesiones de este mes. Desglose calculado con datos diarios.</p>
        </template>
    </section>
</template>

<style scoped>
.charting-engulfing-inspector { position: absolute; z-index: 12; width: 30rem; max-width: calc(100% - 16px); max-height: calc(100% - 16px); box-sizing: border-box; overflow: auto; overscroll-behavior: contain; padding: 1rem; border: 1px solid var(--charting-b-border-color); border-radius: 1rem; background: var(--p-surface-0, var(--color-background, #fff)); color: var(--p-text-color); box-shadow: 0 14px 44px #00000026; scrollbar-width: thin; }
.charting-engulfing-inspector__header { display: flex; justify-content: space-between; align-items: center; gap: 0.5rem; }
.charting-engulfing-inspector__eyebrow { color: var(--p-text-muted-color); font-size: 0.6rem; font-weight: 700; letter-spacing: 0.06em; }
.charting-engulfing-inspector h3 { margin: 0.25rem 0 0; font-size: 1rem; font-weight: 700; }
.charting-engulfing-inspector h3::first-letter { text-transform: uppercase; }
.charting-engulfing-inspector button { color: inherit; font: inherit; cursor: pointer; }
.charting-engulfing-inspector button:focus-visible { outline: 2px solid #8b5cf6; outline-offset: 2px; }
.charting-engulfing-inspector__close { flex-shrink: 0; border: 0; border-radius: 50%; width: 2rem; height: 2rem; background: var(--charting-b-hover-bg); font-size: 1.35rem !important; }
.charting-engulfing-inspector__classification { display: inline-block; margin: 0.8rem 0 0.3rem; border-radius: 1rem; padding: 0.25rem 0.6rem; background: color-mix(in srgb, #8b5cf6 12%, transparent); font-size: 0.75rem; font-weight: 650; }
.charting-engulfing-inspector__classification.is-unknown { background: var(--charting-b-hover-bg); color: var(--p-text-muted-color); }
.charting-engulfing-inspector__provisional { margin-top: 0.7rem; font-size: 0.72rem; color: var(--p-text-muted-color); }
.charting-engulfing-inspector__status { display: flex; justify-content: center; align-items: center; flex-direction: column; gap: 1rem; min-height: 12rem; font-size: 0.85rem; color: var(--p-text-muted-color); }
.charting-engulfing-inspector__status button { border: 1px solid var(--charting-b-border-color); border-radius: 0.5rem; padding: 0.5rem 0.8rem; background: transparent; }
.charting-engulfing-inspector__guide { font-size: 0.7rem; color: var(--p-text-muted-color); }
.charting-engulfing-inspector__events { display: flex; gap: 1rem; margin: 0.85rem 0; padding: 0; list-style: none; }
.charting-engulfing-inspector__events li { display: flex; gap: 0.45rem; align-items: flex-start; font-size: 0.68rem; color: var(--p-text-muted-color); }
.charting-engulfing-inspector__events b { display: grid; place-items: center; border-radius: 50%; width: 1rem; height: 1rem; background: #7c3aed; color: white; font-size: 0.65rem; }
.charting-engulfing-inspector__events strong { display: block; margin-top: 0.15rem; color: var(--p-text-color); font-weight: 600; font-size: 0.75rem; }
.charting-engulfing-inspector__explanation { margin: 0.75rem 0 0; font-size: 0.77rem; line-height: 1.5; }
.charting-engulfing-inspector__footnote { border-top: 1px solid var(--charting-b-border-color); padding-top: 0.65rem; margin: 0.75rem 0 0; font-size: 0.66rem; line-height: 1.45; color: var(--p-text-muted-color); }
@media (max-width: 699px) { .charting-engulfing-inspector { padding: 0.75rem; } .charting-engulfing-inspector__close { width: 2.5rem; height: 2.5rem; } }
</style>
